/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * A component that renders the content of a discussion post.
 *
 * Discussion post types (see Post in types.js) include:
 * - questions,
 * - answers,
 * - comments (referred to as tips & thanks in the UI),
 * - replies (referred to as comments in the ui), and
 * - clarifications (referred to as mistakes in the UI).
 *
 * IMPORTANT: Accepted clarifications are rendered in a different file
 * (at the time of writing it's discussion-post-accepted-clarification.jsx).
 * We need to keep this file in sync with that one.
 */
import {plural, t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import * as React from "react";
import {findDOMNode} from "react-dom";
import {connect} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {ModalLauncher as WBModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    BodyMonospace,
    Caption,
    HeadingXSmall,
    LabelMedium,
    LabelSmall,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";
import {PermissionsInfo} from "@khan/shared-core";
import {a11yStyles, mediaQueries} from "@khan/shared-styles";
import {getLoginUrl, redirect} from "@khan/urls";

import FormattedMarkdown from "#components/formatted-markdown-package/formatted-markdown.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpring from "#components/layout-package/deprecated-spring.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedModalLauncher from "#components/modal-package/deprecated-modal-launcher.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedNewTooltip from "#components/new-tooltip-package/deprecated-new-tooltip.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import * as sharedStyles from "../shared-styles.ts";
import type {
    ContentKind,
    FeedbackType,
    FlagType,
    Post,
    PostKey,
    ServerPost,
    ViewMode,
} from "../types.ts";
import {
    capabilities,
    getAnswerInputId,
    getDirectLinkSrString,
    getPostContext,
    getPostTypeAllowingProgram,
    getRespondButtonSrString,
    isModeratorProfile,
    PostAuthorName,
    shouldAutoExpandAnswers,
    shouldAutoExpandReplies,
    userMeetsCommunityRequirements,
} from "../util.tsx";

import AcceptedClarificationInput from "./accepted-clarification-input.tsx";
import ClearFlagsModal from "./clear-flags-modal.tsx";
import ConfirmDeletionModal from "./confirm-deletion-modal.tsx";
import {
    changeType,
    clearFlags,
    deletePost,
    deselectPost,
    downvotePost,
    fetchAnswers,
    fetchRepliesPage,
    flagPost,
    hideProgramEverywhere,
    hideProgramFromHotlist,
    lockThread,
    modifyNotificationsAction,
    pinThread,
    postSuccessAction,
    resetExpandKeyAction,
    selectPosts,
    skipPosts,
    startAcceptingClarificationAction,
    stopAcceptingClarificationAction,
    toggleReplyInputAction,
    undeletePost,
    unlockThread,
    unpinThread,
    upvotePost,
} from "./discussion-actions.ts";
import DiscussionAvatar from "./discussion-avatar.tsx";
import DiscussionEvaluation from "./discussion-evaluation.tsx";
import DiscussionFlagModal from "./discussion-flag-modal.tsx";
import DiscussionInput from "./discussion-input.tsx";
import AnswerList from "./discussion-post-answer-list.tsx";
import Badges from "./discussion-post-badges.tsx";
import ReplyList, {CommentButton} from "./discussion-post-reply-list.tsx";
import type {State as ReducerState} from "./discussion-reducer.ts";
import ProgramFlag from "./program-flag.tsx";
import ToolButton from "./tool-button.tsx";

import flagIcon from "@phosphor-icons/core/bold/flag-bold.svg";
import triangleIcon from "@phosphor-icons/core/bold/triangle-bold.svg";
import flagSelectedIcon from "@phosphor-icons/core/fill/flag-fill.svg";
import triangleSelectedIcon from "@phosphor-icons/core/fill/triangle-fill.svg";

const {compactStyles} = sharedStyles;

const InlineAction = ({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}): React.ReactElement => (
    <Button
        kind="tertiary"
        size="small"
        style={{marginRight: 12}}
        onClick={onClick}
    >
        {label}
    </Button>
);

type DiscussionPostOwnProps = {
    post: Post;
    postKey: string;
    newPostKey: string | null | undefined;
    resetNewPostKeyInfo: () => void;
    isAnswer?: boolean;
    tab: FeedbackType;
    getRef: (node?: any) => void;
    topicSlug: string;
    displayInputPostKey?: string | null | undefined;
    // How to display posts.
    // - "compact": w/ mobile layout and hidden avatars. Sometimes we want to show compact posts even when the viewport is large (e.g. in the discussions profile widget).
    viewMode: ViewMode;
    // Whether to show the ContentItem associated with the post.
    showPostedIn: boolean;
    // Whether to show the Answer's associated question text
    showParentQuestion: boolean;
    // Whether we should automatically focus on the post element (not the container).
    // autoFocusPostOnMount is only passed in by a parent DiscussionPost component
    // (for example, a question post can tell a reply to automatically focus on mount,
    // but the top-level question will never receive this prop).
    autoFocusPostOnMount?: boolean;
    isMaximized?: boolean;
    restrictPosting: boolean;
    moderatorMode?: boolean;
    clarificationsEnabled?: boolean;
    isProjectFeedback?: boolean;
    includeCheckbox?: boolean;
};

type DiscussionPostReduxProps = {
    qaExpandKey: string | null | undefined;
    contentId: string;
    contentKind: string | null | undefined;
    onUpvotePost: typeof upvotePost;
    onDownvotePost: typeof downvotePost;
    onFlagPost: (
        post: Post,
        flag: FlagType,
        gqlClient: ApolloGqlClient,
    ) => void;
    onPostSuccess: (post: ServerPost, tab: FeedbackType) => void;
    onShowReplies: (
        key: string,
        gqlClient: ApolloGqlClient,
        options?: {
            cursor?: string;
            noneToLoad?: boolean;
        },
    ) => void;
    onShowAnswers: (key: string, gqlClient: ApolloGqlClient) => void;
    onChangeType: typeof changeType;
    onClearFlags: typeof clearFlags;
    onDeletePost: (post: Post, gqlClient: ApolloGqlClient) => void;
    onSkipPosts: typeof skipPosts;
    onHideProgramEverywhere: (
        programID: string,
        hide: boolean,
        gqlClient: ApolloGqlClient,
    ) => void;
    onHideProgramFromHotlist: (
        postKey: PostKey,
        programID: string,
        hide: boolean,
        gqlClient: ApolloGqlClient,
    ) => void;
    onLockThread: (post: Post, gqlClient: ApolloGqlClient) => void;
    onPinThread: (post: Post, gqlClient: ApolloGqlClient) => void;
    onSelectProgramPost: (post: string) => void;
    onUndeletePost: typeof undeletePost;
    onUnlockThread: (post: Post, gqlClient: ApolloGqlClient) => void;
    onUnpinThread: (post: Post, gqlClient: ApolloGqlClient) => void;
    onModifyNotifications: (
        postKey: string,
        on: boolean,
        gqlClient: ApolloGqlClient,
    ) => void;
    onToggleReplyInput: (postKey: string) => void;
    resetExpandKey: () => void;
    clarificationBeingAcceptedId: string | null | undefined;
    onStartAcceptingClarification: (postKey: PostKey) => void;
    onStopAcceptingClarification: () => void;
    onSelectPosts: typeof selectPosts;
    onDeselectPost: typeof deselectPost;
    selected: boolean;
    selectedProgramKey: string;
    fetchingRepliesStatus: Record<
        string,
        "loading" | "complete" | "partial" | "failed"
    >;
};

type DiscussionPostProps = DiscussionPostOwnProps &
    DiscussionPostReduxProps &
    WithActionSchedulerProps &
    WithLocationProps &
    WithUserProfileProps &
    WithApolloProps &
    WithPrefersReducedMotionProps;

type DiscussionPostState = {
    ariaVoteMessage: string;
    ariaFlagMessage: string;
    ariaPostStatusChangedMessage: string;
    repliesExpanded: boolean;
    editing: boolean;
    formattingOptionsExpanded: boolean;
    previewExpanded: boolean;
    // List of keys of posts that have their answers fully visible.
    // If there is more than one answer on the post, when the post first loads,
    // only the first answer will be shown and the rest will be hidden behind a button.
    expandedKeys: Array<string>;
    skipped: boolean;
    // TODO(slim): Switch the "Flag Post" modal to WB and add that here.
    isModalOpen: {
        clearFlags: boolean;
        confirmDeletion: boolean;
    };
    fetchingAnswersStatus: Record<any, any>;
};

const ariaAbstained = (post: Post, num: number): string => {
    if (post.type === "answer") {
        return plural(num, {
            one: "Your vote has been removed. This answer has # vote total.",
            other: "Your vote has been removed. This answer has # votes total.",
        });
    }
    if (post.type === "reply") {
        return plural(num, {
            one: "Your vote has been removed. This comment has # vote total.",
            other: "Your vote has been removed. This comment has # votes total.",
        });
    }
    return plural(num, {
        one: "Your vote has been removed. This post has # vote total.",
        other: "Your vote has been removed. This post has # votes total.",
    });
};

const ariaDownvoted = (post: Post, num: number): string => {
    if (post.type === "answer") {
        return plural(num, {
            one: "You downvoted. This answer has # vote total.",
            other: "You downvoted. This answer has # votes total.",
        });
    }
    if (post.type === "reply") {
        return plural(num, {
            one: "You downvoted. This comment has # vote total.",
            other: "You downvoted. This comment has # votes total.",
        });
    }
    return plural(num, {
        one: "You downvoted. This post has # vote total.",
        other: "You downvoted. This post has # votes total.",
    });
};

const ariaUpvoted = (post: Post, num: number): string => {
    if (post.type === "answer") {
        return plural(num, {
            one: "You upvoted. This answer has # vote total.",
            other: "You upvoted. This answer has # votes total.",
        });
    }
    if (post.type === "reply") {
        return plural(num, {
            one: "You upvoted. This comment has # vote total.",
            other: "You upvoted. This comment has # votes total.",
        });
    }
    return plural(num, {
        one: "You upvoted. This post has # vote total.",
        other: "You upvoted. This post has # votes total.",
    });
};

const getSrOnlyToolOpensModal = (): React.ReactNode => (
    <View style={a11yStyles.srOnly}> {t`Button navigates to signup page`}</View>
);

export class DiscussionPost extends React.Component<
    DiscussionPostProps,
    DiscussionPostState
> {
    postElement: HTMLElement | null | undefined;
    actionMenu: HTMLButtonElement | null | undefined;
    state: DiscussionPostState;

    static getRepliesExpandedFromProps(props: DiscussionPostProps): boolean {
        const {post, qaExpandKey, newPostKey} = props;
        const {replyCount} = post;

        return (
            shouldAutoExpandReplies(post, qaExpandKey) &&
            replyCount != null &&
            replyCount > 0 &&
            !newPostKey
        );
    }

    constructor(props: DiscussionPostProps) {
        super(props);
        this.state = {
            ariaVoteMessage: "",
            ariaFlagMessage: "",
            ariaPostStatusChangedMessage: "",
            repliesExpanded: DiscussionPost.getRepliesExpandedFromProps(props),
            editing: false,
            formattingOptionsExpanded: false,
            previewExpanded: false,
            expandedKeys: [],
            skipped: false,
            isModalOpen: {
                clearFlags: false,
                confirmDeletion: false,
            },
            fetchingAnswersStatus: {},
        };
    }

    componentDidMount() {
        const {qaExpandKey, onShowReplies, onShowAnswers, post, gqlClient} =
            this.props;
        const {replyCount, replies, key, answerCount} = post;
        const {repliesExpanded} = this.state;

        if (shouldAutoExpandAnswers(post, qaExpandKey)) {
            if (this.state.expandedKeys.length !== answerCount) {
                onShowAnswers(key, gqlClient);
                this.expandAnswers(key);
            }
        }

        if (repliesExpanded || shouldAutoExpandReplies(post, qaExpandKey)) {
            if (replyCount && replies.length !== replyCount) {
                onShowReplies(key, gqlClient);
            }
        }

        this.maybeAutoFocusPost();
    }

    componentDidUpdate(prevProps: DiscussionPostProps) {
        const prevRepliesExpanded =
            DiscussionPost.getRepliesExpandedFromProps(prevProps);
        const thisRepliesExpanded = DiscussionPost.getRepliesExpandedFromProps(
            this.props,
        );
        if (!prevRepliesExpanded && thisRepliesExpanded) {
            /**
             * As per the React documentation, it is perfectly fine to call
             * setState from componentDidUpdate as long as it is guarded
             * with a condition that does not rely on the state being set.
             *
             * https://reactjs.org/docs/react-component.html#static-getderivedstatefromprops
             * > If you need to perform a side effect (for example, data
             * > fetching or an animation) in response to a change in props,
             * > use componentDidUpdate lifecycle instead.
             *
             * https://reactjs.org/docs/react-component.html#componentdidupdate
             * > You may call setState() immediately in componentDidUpdate()
             * > but note that it must be wrapped in a condition, or you’ll
             * > cause an infinite loop.
             */

            this.setState({
                repliesExpanded: true,
            });
        }

        this.maybeAutoFocusPost(prevProps);
    }

    nodes: Record<string, HTMLLIElement | null | undefined> = {};

    getRepliesContainerId: () => string = () => {
        return `${this.props.post.key}-replies-container`;
    };

    setModalVisibility: (
        shouldBeOpen: boolean,
    ) => (modal: keyof DiscussionPostState["isModalOpen"]) => void =
        (shouldBeOpen: boolean) =>
        (modal: keyof DiscussionPostState["isModalOpen"]) => {
            this.setState((prevState) => ({
                isModalOpen: {
                    ...prevState.isModalOpen,
                    [modal as string]: shouldBeOpen,
                },
            }));
        };

    openModal: (modal: keyof DiscussionPostState["isModalOpen"]) => void =
        this.setModalVisibility(true);
    closeModal: (modal: keyof DiscussionPostState["isModalOpen"]) => void =
        this.setModalVisibility(false);

    redirectToSignup: () => void = () => {
        const {pathname, search, hash} = this.props.location;
        const url = pathname + search + hash;
        redirect(getLoginUrl(url));
    };

    maybeAutoFocusPost: (prevProps?: DiscussionPostProps) => void = (
        prevProps?: DiscussionPostProps,
    ): void => {
        if (!this.postElement || !this.postElement.focus) {
            return;
        }

        const {
            post,
            qaExpandKey,
            newPostKey,
            resetExpandKey,
            resetNewPostKeyInfo,
        } = this.props;

        // The user just submitted or edited this post.
        if (newPostKey && newPostKey === post.key) {
            this.postElement.focus();
            resetNewPostKeyInfo();
            return;
        }

        // The user followed a link to this post.
        if (qaExpandKey && post.expandKey === qaExpandKey) {
            this.postElement.focus();
            resetExpandKey();
            return;
        }

        // The user just opened a list of replies or comments and this is
        // the first post in the newly-revealed list.
        if (
            this.postElement &&
            (!prevProps || !prevProps.autoFocusPostOnMount) &&
            this.props.autoFocusPostOnMount
        ) {
            this.postElement.focus();
            return;
        }
    };

    renderFlagsTooltipContent(): React.ReactElement {
        const {post} = this.props;
        const {flaggedBy, flags, key} = post;

        const flagKaids = flaggedBy || [];
        const rows = flagKaids.map((kaid: string, i: number) => ({
            kaid,
            flag: flags && flags[i],
        }));

        const playgroundLink = (
            <Link
                href={`/devadmin/discussion/mod/playground?feedbackKey=${encodeURIComponent(key)}`}
                rel="nofollow"
                target="_blank"
            >
                {t`View in Playground`}
            </Link>
        );

        return (
            <View style={{margin: spacing.xSmall_8}}>
                {rows.length > 0 ? (
                    <ul>
                        {rows.map((row, i) => (
                            <li key={i}>
                                <View style={styles.flagsTooltipRow}>
                                    <LabelSmall style={styles.flagsTooltipKaid}>
                                        {row.kaid}:
                                    </LabelSmall>
                                    <LabelSmall style={styles.flagsTooltipText}>
                                        {row.flag || t`Unknown`}
                                    </LabelSmall>
                                </View>
                            </li>
                        ))}
                        <li>{playgroundLink}</li>
                    </ul>
                ) : (
                    <LabelSmall style={styles.flagsTooltipText}>
                        {doNotTranslate`No flags on this post`}
                        <View style={styles.flagsTooltipRow}>
                            {playgroundLink}
                        </View>
                    </LabelSmall>
                )}
            </View>
        );
    }

    renderMinimalFlags(
        flagCountText: string,
        flagTextStyles: any,
    ): React.ReactNode {
        return (
            <Tooltip content={this.renderFlagsTooltipContent()}>
                <LabelXSmall style={[flagTextStyles, styles.moderatorFlagged]}>
                    {t`Flagged`}
                    {flagCountText}
                </LabelXSmall>
            </Tooltip>
        );
    }

    renderFlagButton(profile: ProfileModelType | null): React.ReactNode {
        const {onFlagPost, post, tab, gqlClient} = this.props;

        const flagTextStyles = [
            styles.flagText,
            this.isCompactView() && compactStyleSheet.flagText,
        ];

        // TODO(kevinb): sanitize data from server so that
        // flags is an empty array if it doesn't exist.
        const flags = post.flags || [];
        const flagCountText = ` (${flags.length})`;

        // Moderators just see a flag summary without the ability to flag
        if (isModeratorProfile(profile)) {
            return this.renderMinimalFlags(flagCountText, flagTextStyles);
        }

        const shouldShowTooltip =
            profile &&
            !profile.get("isPhantom") &&
            !userMeetsCommunityRequirements(profile);

        const flagColor = post.flaggedByUser
            ? color.blue
            : shouldShowTooltip
              ? color.offBlack32
              : color.offBlack64;

        return (
            <Id>
                {(idRoot) => (
                    <DeprecatedModalLauncher
                        modal={({closeModal}) => (
                            <DiscussionFlagModal
                                post={post}
                                tab={tab}
                                onClose={(flag?: FlagType | null) => {
                                    if (flag) {
                                        onFlagPost(post, flag, gqlClient);
                                        this.setState({
                                            ariaFlagMessage: t`You flagged this post.`,
                                        });
                                    }
                                    closeModal();
                                }}
                            />
                        )}
                        ariaLabel={t`Modal to flag content`}
                        focusId={`${idRoot}flag`}
                    >
                        {(showModal) => {
                            return (
                                <DeprecatedNewTooltip
                                    content={
                                        shouldShowTooltip &&
                                        t`To flag, your account must be at least 3 days old, have a verified email address, and have at least 5,000 energy points.`
                                    }
                                    toggleOnHover={!!shouldShowTooltip}
                                    offset={12}
                                    a11y={{
                                        title: {id: "flag"},
                                    }}
                                >
                                    <ToolButton
                                        id={`${idRoot}flag`}
                                        icon={
                                            <PhosphorIcon
                                                size="small"
                                                icon={
                                                    post.flaggedByUser
                                                        ? flagSelectedIcon
                                                        : flagIcon
                                                }
                                                color={flagColor}
                                            />
                                        }
                                        style={[
                                            styles.toolButton,
                                            styles.flagTool,
                                        ]}
                                        disabled={
                                            post.flaggedByUser ||
                                            !!shouldShowTooltip
                                        }
                                        onClick={() => {
                                            if (PermissionsInfo.isLoggedIn) {
                                                showModal();
                                            } else {
                                                this.redirectToSignup();
                                            }
                                        }}
                                    >
                                        <View style={flagTextStyles}>
                                            {post.flaggedByUser
                                                ? t`Flagged`
                                                : t`Flag`}
                                            {isModeratorProfile(profile) &&
                                                flagCountText}
                                            {!PermissionsInfo.isLoggedIn ? (
                                                getSrOnlyToolOpensModal()
                                            ) : (
                                                <View style={a11yStyles.srOnly}>
                                                    {" "}
                                                    {t`Opens a modal to flag content`}
                                                </View>
                                            )}
                                        </View>
                                    </ToolButton>
                                </DeprecatedNewTooltip>
                            );
                        }}
                    </DeprecatedModalLauncher>
                )}
            </Id>
        );
    }

    handleUpvote: () => void = () => {
        if (!PermissionsInfo.isLoggedIn) {
            this.redirectToSignup();
            return;
        }

        const {post, gqlClient, onUpvotePost} = this.props;
        const {key, sumVotesIncremented, upVoted, downVoted} = post;

        onUpvotePost({key, upVoted: !!upVoted}, gqlClient);

        if (upVoted) {
            const count = sumVotesIncremented - 1;
            this.setState({
                ariaVoteMessage: ariaAbstained(post, count),
            });
        } else {
            const count = sumVotesIncremented + (downVoted ? 2 : 1);
            this.setState({
                ariaVoteMessage: ariaUpvoted(post, count),
            });
        }
    };

    handleDownvote: () => void = () => {
        if (!PermissionsInfo.isLoggedIn) {
            this.redirectToSignup();
            return;
        }

        const {post, gqlClient, onDownvotePost} = this.props;
        const {key, sumVotesIncremented, downVoted, upVoted} = post;

        onDownvotePost({key, downVoted: !!downVoted}, gqlClient);

        if (downVoted) {
            const count = sumVotesIncremented + 1;
            this.setState({
                ariaVoteMessage: ariaAbstained(post, count),
            });
        } else {
            const count = sumVotesIncremented - (upVoted ? 2 : 1);
            this.setState({
                ariaVoteMessage: ariaDownvoted(post, count),
            });
        }
    };

    handleChangeToQuestion: () => void = () => {
        const {onChangeType, moderatorMode, post, gqlClient} = this.props;
        onChangeType(post, "question", gqlClient);
        this.handleSelectionChange(false);
        // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
        this.setState({skipped: moderatorMode});
    };

    handleChangeToTipsAndThanks: () => void = () => {
        const {onChangeType, moderatorMode, post, gqlClient} = this.props;
        onChangeType(post, "comment", gqlClient);
        this.handleSelectionChange(false);
        // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
        this.setState({skipped: moderatorMode});
    };

    handleChangeToMistakes: () => void = () => {
        const {onChangeType, moderatorMode, post, gqlClient} = this.props;
        onChangeType(post, "clarification", gqlClient);
        this.handleSelectionChange(false);
        // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
        this.setState({skipped: moderatorMode});
    };

    handleClearAllFlags: () => void = () => {
        const {moderatorMode, onClearFlags, post, gqlClient} = this.props;
        onClearFlags(post, gqlClient);

        if (moderatorMode) {
            this.handleSelectionChange(false);
            this.setState({skipped: true});
        }
    };

    handleUndelete: () => void = () => {
        const {onUndeletePost, post, gqlClient} = this.props;
        onUndeletePost(post, gqlClient);

        this.setState({
            ariaPostStatusChangedMessage: t`Post restored successfully.`,
        });
    };

    handleDelete: () => void = () => {
        const {moderatorMode, onDeletePost, post, gqlClient} = this.props;
        onDeletePost(post, gqlClient);
        this.handleSelectionChange(false);
        this.setState({
            ariaPostStatusChangedMessage: t`Post deleted successfully.`,
            // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
            skipped: moderatorMode,
        });
    };

    handleLockThread: () => void = () => {
        const {onLockThread, post, gqlClient} = this.props;
        onLockThread(post, gqlClient);
    };

    handleUnlockThread: () => void = () => {
        const {onUnlockThread, post, gqlClient} = this.props;
        onUnlockThread(post, gqlClient);
    };

    handlePinThread: () => void = () => {
        const {onPinThread, post, gqlClient} = this.props;
        onPinThread(post, gqlClient);
    };

    handleUnpinThread: () => void = () => {
        const {onUnpinThread, post, gqlClient} = this.props;
        onUnpinThread(post, gqlClient);
    };

    handleHideProgramEverywhere: () => void = () => {
        const {moderatorMode, onHideProgramEverywhere, post, gqlClient} =
            this.props;
        const {programID, deleted} = post;

        if (!programID) {
            return;
        }
        const hide = !deleted;

        onHideProgramEverywhere(programID, hide, gqlClient);

        if (moderatorMode) {
            this.setState({skipped: true});
        }
    };

    handleHideProgramFromHotlist: () => void = () => {
        const {onHideProgramFromHotlist, post, gqlClient} = this.props;
        const {key, programID, hideFromHotlist} = post;

        if (!programID) {
            return;
        }
        const hide = !hideFromHotlist;

        onHideProgramFromHotlist(key, programID, hide, gqlClient);
    };

    handleSkip: () => void = () => {
        const {onSkipPosts, post} = this.props;
        this.handleSelectionChange(false);
        onSkipPosts([post.key]);
        this.setState({skipped: true});
    };

    handleClickComment: () => void = () => {
        const {post, onShowReplies, gqlClient} = this.props;
        const {replyCount} = post;

        const noneToLoad = !replyCount || replyCount === 0;

        if (!this.state.repliesExpanded) {
            onShowReplies(this.props.post.key, gqlClient, {noneToLoad});
        }
        if (!PermissionsInfo.isLoggedIn && noneToLoad) {
            this.redirectToSignup();
        } else {
            this.setState({
                repliesExpanded: !this.state.repliesExpanded,
            });
        }
    };

    handleReplyInputToggle: () => void = () => {
        const {displayInputPostKey, post} = this.props;
        this.props.onToggleReplyInput(post.key);
        if (displayInputPostKey === post.key) {
            this.setState({
                formattingOptionsExpanded: false,
            });
        }
    };

    isCompactView(): boolean {
        const {viewMode} = this.props;
        return viewMode === "compact";
    }

    showFlagButton: (profile?: any) => any | boolean = (profile: any) => {
        const {post, profileLoading} = this.props;

        const type = getPostTypeAllowingProgram(post);
        const isProjectEval = type === "projecteval";

        if (isProjectEval) {
            return false;
        }

        // In profiles, where compact view is used, we only show the Flag button for Comments (`replies`).
        if (this.isCompactView() && type !== "reply") {
            return false;
        }

        // Logged out users and users at least 13 years of age can see the flag buttons.
        // Logged out users can't vote, but they do see the buttons. The buttons
        // just open the signup modal instead of flagging the post.
        // We also hide the flag button when the profile is loading to avoid a flash
        // of a button that then disappears.
        return (
            !profileLoading &&
            (!profile || (profile && !profile.get("isChildAccount")))
        );
    };

    shouldShowAuthor(): boolean {
        const {post, moderatorMode} = this.props;
        const isProjectEvalAnswer =
            getPostTypeAllowingProgram(post) === "projectevalanswer";
        if (moderatorMode) {
            return true;
        }

        return !this.isCompactView() && !isProjectEvalAnswer;
    }

    renderOpenModals(): React.ReactNode {
        // TODO(diedra): Don't render modal launchers if the user is not an admin.

        const {post, schedule} = this.props;
        const {isModalOpen} = this.state;

        return (
            <React.Fragment>
                <WBModalLauncher
                    key="deletePost"
                    onClose={() => {
                        this.closeModal("confirmDeletion");
                        schedule.animationFrame(() => {
                            this.actionMenu && this.actionMenu.focus();
                        });
                    }}
                    opened={isModalOpen.confirmDeletion}
                    modal={({closeModal}) => (
                        <ConfirmDeletionModal
                            handleClose={closeModal}
                            handleSubmit={() => {
                                this.handleDelete();
                                closeModal();
                            }}
                        />
                    )}
                />
                <WBModalLauncher
                    key="clearFlags"
                    onClose={() => this.closeModal("clearFlags")}
                    opened={isModalOpen.clearFlags}
                    modal={({closeModal}) => (
                        <ClearFlagsModal
                            flaggedBy={
                                post.flaggedBy || post.flaggedByKaid || []
                            }
                            flags={post.flags || []}
                            onCancel={closeModal}
                            onClearAllFlags={this.handleClearAllFlags}
                        />
                    )}
                />
            </React.Fragment>
        );
    }

    renderDeleteAction(
        profile: ProfileModelType | null,
        ActionUI: React.ComponentType<
            JSX.LibraryManagedAttributes<
                typeof InlineAction,
                React.ComponentProps<typeof InlineAction>
            >
        >,
    ): React.ReactNode {
        // Mods have the ability to un-delete posts, so they can bypass the confirmation.
        const handleClick = isModeratorProfile(profile)
            ? this.handleDelete
            : // Everyone else sees a confirmation dialog.
              () => this.openModal("confirmDeletion");

        return (
            <ActionUI key="delete" label={t`Delete`} onClick={handleClick} />
        );
    }

    renderHideProgramActions(
        post: Post,
        ActionUI: React.ComponentType<
            JSX.LibraryManagedAttributes<
                typeof InlineAction,
                React.ComponentProps<typeof InlineAction>
            >
        >,
    ): Array<React.ReactNode> {
        const hideEverywhereText = post.deleted
            ? t`Undo hide everywhere`
            : t`Hide everywhere`;

        const hideHotlistText = post.hideFromHotlist
            ? t`Undo hide from hotlist`
            : t`Hide from hotlist`;

        return [
            <ActionUI
                key="hideEverywhere"
                label={hideEverywhereText}
                onClick={this.handleHideProgramEverywhere}
            />,
            <ActionUI
                key="hideHotlist"
                label={hideHotlistText}
                onClick={this.handleHideProgramFromHotlist}
            />,
        ];
    }

    // TODO(diedra): Move this into new component.
    // Render "Turn notifications on/off", "edit" and "delete" buttons on your
    // own posts or on all posts when logged in as a moderator.
    getModeratorOrOwnerActions(
        profile: ProfileModelType | null,
        ActionUI: React.ComponentType<
            JSX.LibraryManagedAttributes<
                typeof InlineAction,
                React.ComponentProps<typeof InlineAction>
            >
        >,
    ): Array<React.ReactNode> {
        const {moderatorMode, isProjectFeedback} = this.props;
        if (!profile) {
            return [];
        }

        const {
            post,
            tab,
            onModifyNotifications,
            contentKind,
            onStartAcceptingClarification,
            gqlClient,
        } = this.props;

        const authorKaid = post.author?.kaid ?? post.authorKaid;
        const type = getPostTypeAllowingProgram(post);

        const isOwnPost = getKaid(profile) === authorKaid;
        const isReply = type === "reply";
        const isScratchpad = type === "scratchpad";
        const isProjectEval = type === "projecteval";
        const isProjectEvalAnswer = type === "projectevalanswer";
        const isQuestion = type === "question";
        const isAnswer = type === "answer";
        const isTipsAndThanks = type === "comment";
        // These two types are the roots of discussion threads, which can have
        // answers and replies as their children.
        // In the UI, "comment" is "tips and thanks" and "reply" is "comment".
        // Isn't that fun? "Answer" used to be "reply", but we fixed that one.
        const isThreadRoot = isQuestion || isTipsAndThanks;
        // We only allow pinning of questions, tips and thanks, and answers.
        const isPinnable = isQuestion || isTipsAndThanks || isAnswer;

        if (!isModeratorProfile(profile) && !isOwnPost) {
            return [];
        }

        const items: Array<React.ReactElement> = [];

        const postContext = getPostContext({
            profile,
            post,
            moderatorMode: !!moderatorMode,
        });

        // TODO(WB-439): Dropdown menus should accept falsy items
        if (
            isModeratorProfile(profile) &&
            !isReply &&
            !isScratchpad &&
            !isProjectEvalAnswer &&
            !isProjectEval
        ) {
            if (tab === "comments" || tab === "clarifications") {
                items.push(
                    <ActionUI
                        key="changeToQuestion"
                        onClick={this.handleChangeToQuestion}
                        label={t`Change to question`}
                    />,
                );
            }

            if (tab === "questions" || tab === "clarifications") {
                items.push(
                    <ActionUI
                        key="changeToTipsAndThanks"
                        onClick={this.handleChangeToTipsAndThanks}
                        label={t`Change to tip/thanks`}
                    />,
                );
            }

            if (
                !isProjectFeedback &&
                (tab === "questions" || tab === "comments")
            ) {
                items.push(
                    <ActionUI
                        key="changeToMistakes"
                        onClick={this.handleChangeToMistakes}
                        label={t`Change to mistake`}
                    />,
                );
            }
        }

        // lock
        if (
            isModeratorProfile(profile) &&
            isThreadRoot &&
            !post.deleted &&
            !post.isLocked
        ) {
            items.push(
                <ActionUI
                    key="lock"
                    label={t`Lock`}
                    onClick={this.handleLockThread}
                />,
            );
        }

        // unlock
        if (
            isModeratorProfile(profile) &&
            isThreadRoot &&
            !post.deleted &&
            post.isLocked
        ) {
            items.push(
                <ActionUI
                    key="unlock"
                    label={t`Unlock`}
                    onClick={this.handleUnlockThread}
                />,
            );
        }

        // pin
        if (
            isModeratorProfile(profile) &&
            isPinnable &&
            !post.deleted &&
            !post.isPinned
        ) {
            items.push(
                <ActionUI
                    key="pin"
                    label={t`Pin`}
                    onClick={this.handlePinThread}
                />,
            );
        }

        // unpin
        if (
            isModeratorProfile(profile) &&
            isPinnable &&
            !post.deleted &&
            post.isPinned
        ) {
            items.push(
                <ActionUI
                    key="unpin"
                    label={t`Unpin`}
                    onClick={this.handleUnpinThread}
                />,
            );
        }

        const notificationCopy = post.notifyOnAnswer
            ? t`Turn notifications off`
            : t`Turn notifications on`;
        if (isQuestion && isOwnPost && !post.isOld) {
            items.push(
                <ActionUI
                    key="notification"
                    label={notificationCopy}
                    onClick={() => {
                        onModifyNotifications(
                            post.key,
                            !post.notifyOnAnswer,
                            gqlClient,
                        );
                    }}
                />,
            );
        }

        if (
            !isScratchpad &&
            !isProjectEval &&
            !isProjectEvalAnswer &&
            // if locked, only mods can edit
            (!post.isLocked || isModeratorProfile(profile))
        ) {
            items.push(
                <ActionUI
                    key="edit"
                    label={t`Edit`}
                    onClick={() => this.setState({editing: true})}
                />,
            );
        }

        if (!post.deleted && !isScratchpad) {
            // @ts-expect-error - TS2345 - Argument of type 'ReactNode' is not assignable to parameter of type 'never'.
            items.push(this.renderDeleteAction(profile, ActionUI));
        }

        if (isScratchpad) {
            // @ts-expect-error - TS2345 - Argument of type 'ReactNode' is not assignable to parameter of type 'never'.
            items.push(...this.renderHideProgramActions(post, ActionUI));
        }

        if (isModeratorProfile(profile) && post.deleted && !isScratchpad) {
            items.push(
                <ActionUI
                    key="undelete"
                    label={t`Undelete`}
                    onClick={this.handleUndelete}
                />,
            );
        }

        if (capabilities.clearFlags(postContext)) {
            items.push(
                <ActionUI
                    key="clearFlags2"
                    label={t`Clear flags`}
                    onClick={() => this.openModal("clearFlags")}
                />,
            );
        }

        if (capabilities.approve(postContext)) {
            items.push(
                <ActionUI
                    key="approve"
                    label={t`Approve`}
                    onClick={this.handleClearAllFlags}
                />,
            );
        }

        if (moderatorMode && isModeratorProfile(profile)) {
            items.push(
                <ActionUI
                    key="skip"
                    label={t`Skip`}
                    onClick={this.handleSkip}
                />,
            );
        }

        if (
            isModeratorProfile(profile) &&
            tab === "clarifications" &&
            (contentKind === "Video" ||
                contentKind === "video" ||
                contentKind === "YouTubeVideo")
        ) {
            items.push(
                <ActionUI
                    key="accept_clarification"
                    label={t`Accept clarification`}
                    onClick={() => onStartAcceptingClarification(post.key)}
                />,
            );
        }

        return items;
    }

    renderModeratorOrOwnerActionsAsDropdown(
        profile: ProfileModelType | null,
        style: any,
    ): React.ReactNode {
        // @ts-expect-error - TS2345 - Argument of type 'typeof ActionItem' is not assignable to parameter of type 'ComponentType<{ label: string; onClick: () => void; }>'.
        const items = this.getModeratorOrOwnerActions(profile, ActionItem);

        const actionMenuRef = (node?: ActionMenu | null) => {
            const menu = findDOMNode(node) as HTMLElement;
            if (this.props.getRef) {
                this.props.getRef(menu);
            }
            const button = menu && menu.querySelector("button");
            if (button instanceof HTMLButtonElement) {
                this.actionMenu = button;
            }
        };

        return (
            <ActionMenu
                ref={actionMenuRef}
                menuText={t`more`}
                alignment="right"
                style={style}
            >
                {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                {items}
            </ActionMenu>
        );
    }

    renderModeratorOrOwnerActionsAsFlat(
        profile: ProfileModelType | null,
        style: any,
    ): React.ReactNode {
        const items = this.getModeratorOrOwnerActions(profile, InlineAction);

        return (
            <View style={[style, styles.expandedActionList]}>
                {items.map((item: React.ReactNode, i) => {
                    return <View key={i}>{item}</View>;
                })}
            </View>
        );
    }

    renderModeratorOrOwnerActions(
        profile: ProfileModelType | null,
        style: any,
    ): React.ReactNode {
        const {isMaximized} = this.props;

        return isMaximized
            ? this.renderModeratorOrOwnerActionsAsFlat(profile, style)
            : this.renderModeratorOrOwnerActionsAsDropdown(profile, style);
    }

    // Render voting and flagging tools
    renderTools(profile: ProfileModelType | null): React.ReactNode {
        const {post, restrictPosting} = this.props;

        const type = getPostTypeAllowingProgram(post);
        const isProjectEval = type === "projecteval";
        const isProjectEvalAnswer = type === "projectevalanswer";
        const isReply = getPostTypeAllowingProgram(post) === "reply";

        const showVoteTools =
            !this.isCompactView() &&
            !isProjectEval &&
            !isProjectEvalAnswer &&
            !isReply &&
            !restrictPosting;

        const flagButtonAsLi = <li>{this.renderFlagButton(profile)}</li>;

        return (
            (showVoteTools || this.showFlagButton(profile)) && (
                <React.Fragment>
                    <ul className={css(styles.voteFlagList)}>
                        {showVoteTools && this.renderVoteTools(profile)}
                        {this.showFlagButton(profile) && flagButtonAsLi}
                    </ul>
                    <View
                        style={a11yStyles.srOnly}
                        role="status"
                        aria-live="polite"
                    >
                        {showVoteTools && (
                            <View>{this.state.ariaVoteMessage}</View>
                        )}
                        {this.showFlagButton(profile) && (
                            <View>{this.state.ariaFlagMessage}</View>
                        )}
                    </View>
                </React.Fragment>
            )
        );
    }

    renderVoteTools(profile: any): React.ReactNode {
        const {post} = this.props;
        let reason: string | null = null;
        if (profile && !profile.get("isPhantom")) {
            if (
                profile.get("kaid") === (post.author?.kaid ?? post.authorKaid)
            ) {
                reason = t`You can't vote for your own posts`;
            } else if (
                profile.get("points") < 5000 &&
                !isModeratorProfile(profile)
            ) {
                reason = t`You need at least 5000 energy points to vote`;
            }
        }
        const disableTools = !!reason;

        const upVoteColor = disableTools
            ? color.offBlack32
            : post.upVoted
              ? color.green
              : color.offBlack64;

        const downVoteColor = disableTools
            ? color.offBlack32
            : post.downVoted
              ? color.red
              : color.offBlack64;

        const upVoteTitle = post.upVoted
            ? t`You've upvoted this post.`
            : undefined;

        const downVoteTitle = post.downVoted
            ? t`You've downvoted this post.`
            : undefined;

        return (
            <React.Fragment>
                <li>
                    <DeprecatedNewTooltip
                        content={reason}
                        toggleOnHover={disableTools}
                        offset={12}
                        a11y={{
                            title: {id: "upvote"},
                        }}
                    >
                        <ToolButton
                            icon={
                                <PhosphorIcon
                                    aria-label={upVoteTitle}
                                    size="small"
                                    icon={
                                        post.upVoted
                                            ? triangleSelectedIcon
                                            : triangleIcon
                                    }
                                    color={upVoteColor}
                                />
                            }
                            style={styles.toolButton}
                            onClick={() => this.handleUpvote()}
                            disabled={disableTools}
                            aria-labelledby="upvote"
                        >
                            <View style={styles.voteText}>
                                {t`Upvote`}
                                {!PermissionsInfo.isLoggedIn &&
                                    getSrOnlyToolOpensModal()}
                                {PermissionsInfo.isLoggedIn && reason && (
                                    <span className={css(a11yStyles.srOnly)}>
                                        {reason}
                                    </span>
                                )}
                            </View>
                        </ToolButton>
                    </DeprecatedNewTooltip>
                </li>
                <li>
                    <DeprecatedNewTooltip
                        content={reason}
                        toggleOnHover={disableTools}
                        offset={12}
                        a11y={{
                            title: {id: "downvote"},
                        }}
                    >
                        <ToolButton
                            icon={
                                <PhosphorIcon
                                    aria-label={downVoteTitle}
                                    size="small"
                                    icon={
                                        post.downVoted
                                            ? triangleSelectedIcon
                                            : triangleIcon
                                    }
                                    style={styles.downvoteIcon}
                                    color={downVoteColor}
                                />
                            }
                            style={styles.toolButton}
                            onClick={() => this.handleDownvote()}
                            disabled={disableTools}
                            aria-labelledby="downvote"
                        >
                            <View style={styles.voteText}>
                                {t`Downvote`}
                                {!PermissionsInfo.isLoggedIn &&
                                    getSrOnlyToolOpensModal()}
                                {PermissionsInfo.isLoggedIn && reason && (
                                    <span className={css(a11yStyles.srOnly)}>
                                        {reason}
                                    </span>
                                )}
                            </View>
                        </ToolButton>
                    </DeprecatedNewTooltip>
                </li>
            </React.Fragment>
        );
    }

    renderPostHeader: () => React.ReactNode = () => {
        const {post, showPostedIn, showParentQuestion, moderatorMode, profile} =
            this.props;
        const {
            expandKey,
            author,
            feedbackType,
            authorNickname,
            authorKaid,
            date,
            badges,
            permalink,
            focus,
            updated,
            isLocked,
            isPinned,
        } = post;

        const datetimeStyles = [
            sharedStyles.datetime,
            !this.shouldShowAuthor() && compactStyles.datetime,
        ];

        const isReply = getPostTypeAllowingProgram(post) === "reply";
        const isProgram = getPostTypeAllowingProgram(post) === "scratchpad";

        const timestampTextVisual = moderatorMode
            ? moment(date).toString()
            : moment(date).fromNow();
        const timestampTextSr = getDirectLinkSrString(
            timestampTextVisual,
            post,
        );

        const timestamp = (
            <LabelMedium style={datetimeStyles}>
                <View aria-hidden="true">{timestampTextVisual}</View>
                <View style={a11yStyles.srOnly}>{timestampTextSr}</View>
            </LabelMedium>
        );

        let updatedTimestamp = null;
        if (isProgram && updated) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            updatedTimestamp = (
                <LabelMedium style={datetimeStyles}>
                    {t`Last updated`}{" "}
                    {moment(updated).format("MMM DD YYYY, h:mma")}
                </LabelMedium>
            );
        }

        const focusUrlWithExpandKeyAndType =
            permalink && expandKey && feedbackType
                ? `${permalink}?qa_expand_key=${expandKey}&qa_expand_type=${feedbackType.toLowerCase()}`
                : "";

        let deletedOrHiddenLabel = null;
        if (post.deleted) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            deletedOrHiddenLabel = (
                <HeadingXSmall style={styles.deletedPostLabel}>
                    {t`Deleted`}
                </HeadingXSmall>
            );
        } else if (post.appearsAsDeleted) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            deletedOrHiddenLabel = (
                <HeadingXSmall style={styles.hiddenPostLabel}>
                    {t`Automatically hidden`}
                </HeadingXSmall>
            );
        }

        const nameAndDatetimeStyles = [
            sharedStyles.nameAndDatetime,
            this.isCompactView() && compactStyleSheet.nameAndDatetime,
        ];

        return (
            <View style={[styles.postHeader, isReply && styles.replyHeader]}>
                <View>
                    <View style={nameAndDatetimeStyles}>
                        {this.shouldShowAuthor() && (
                            <PostAuthorName
                                name={author?.nickname ?? authorNickname}
                                kaid={author?.kaid ?? authorKaid}
                            />
                        )}

                        <Link
                            href={focusUrlWithExpandKeyAndType}
                            rel="nofollow"
                        >
                            {timestamp}
                        </Link>
                        {updatedTimestamp}
                        {deletedOrHiddenLabel}
                        <View
                            style={a11yStyles.srOnly}
                            role="status"
                            aria-live="polite"
                        >
                            {this.state.ariaPostStatusChangedMessage}
                        </View>
                    </View>
                    {showPostedIn && !!focus && (
                        <View style={styles.postedInContainer}>
                            <LabelMedium style={styles.postedInLabel}>
                                {t`Posted in`}
                            </LabelMedium>
                            <LabelMedium>
                                <Link
                                    href={focusUrlWithExpandKeyAndType}
                                    rel="nofollow"
                                >
                                    {focus.translatedTitle || t`Untitled`}
                                </Link>
                            </LabelMedium>
                        </View>
                    )}
                    {showParentQuestion && post.question && (
                        <View style={styles.postedInContainer}>
                            <LabelMedium style={styles.postedInLabel}>
                                {t`Question`}:
                            </LabelMedium>
                            <Caption>
                                {post.question && post.question.content}
                            </Caption>
                        </View>
                    )}
                </View>
                <Badges
                    badges={badges}
                    isLocked={isLocked}
                    isPinned={isPinned}
                />
                {this.renderModeratorOrOwnerActionsAsDropdown(
                    profile,
                    sharedStyles.actionPickerMobile,
                )}
            </View>
        );
    };

    renderAnswerButton: () => React.ReactNode = () => {
        const {displayInputPostKey, post} = this.props;

        const labelSrString = getRespondButtonSrString(post, "answer");

        const id = `${post.key}-answer-button`;
        return PermissionsInfo.isLoggedIn ? (
            <>
                <Button
                    id={id}
                    kind="tertiary"
                    onClick={this.handleReplyInputToggle}
                    aria-expanded={
                        displayInputPostKey === post.key ? "true" : "false"
                    }
                    aria-controls={getAnswerInputId(post.key)}
                >
                    {t`Answer`}
                </Button>
                <label className={css(a11yStyles.srOnly)} htmlFor={id}>
                    {labelSrString}
                </label>
            </>
        ) : (
            <>
                <Button id={id} kind="tertiary" onClick={this.redirectToSignup}>
                    {t`Answer`}
                </Button>
                <label className={css(a11yStyles.srOnly)} htmlFor={id}>
                    {t`Button navigates to signup page`}
                </label>
            </>
        );
    };

    renderPostFooter: () => React.ReactNode = () => {
        const {post, restrictPosting, isMaximized, profile} = this.props;
        const {repliesExpanded} = this.state;

        const type = getPostTypeAllowingProgram(post); // while we are migrating from REST to GQL

        const isReply = type === "reply";
        const isQuestion = type === "question";
        const isHelpRequest = type === "projecthelp";
        const isProjectEval = type === "projecteval";
        const isProjectEvalAnswer = type === "projectevalanswer";
        const isProgram = type === "scratchpad";

        const hideContributionButtons =
            restrictPosting || this.isCompactView() || post.isLocked;

        // If the user does not have posting privileges, do NOT show "Answer" and "Comment".
        // If we are in Compact View, do NOT show "Answer" and "Comment".
        // If the content is a program, do NOT show "Answer" and "Comment".
        // If the content is locked, do NOT show "Answer" and "Comment".
        const primaryRow = (
            <View style={styles.postFooterPrimary}>
                {(isQuestion || isHelpRequest) && !hideContributionButtons && (
                    <React.Fragment>
                        {this.renderAnswerButton()}
                        <LabelMedium
                            style={styles.separator}
                            aria-hidden="true"
                        >
                            •
                        </LabelMedium>
                    </React.Fragment>
                )}
                {!restrictPosting &&
                    !isReply &&
                    !isProjectEval &&
                    !isProjectEvalAnswer &&
                    !this.isCompactView() && (
                        <CommentButton
                            post={post}
                            isExpanded={repliesExpanded}
                            onClick={this.handleClickComment}
                            containerId={this.getRepliesContainerId()}
                            hideContributionButtons={!!hideContributionButtons}
                        />
                    )}
                <DeprecatedSpring />
                <View style={sharedStyles.tools}>
                    {!isReply && !isProjectEval && !isProjectEvalAnswer && (
                        <LabelXSmall style={styles.votesSum}>
                            (
                            {plural(post.sumVotesIncremented, {
                                one: "# vote",
                                other: "# votes",
                            })}
                            )
                        </LabelXSmall>
                    )}
                    {this.renderTools(profile)}
                </View>
                {!isMaximized &&
                    this.renderModeratorOrOwnerActions(
                        profile,
                        sharedStyles.actionPickerDesktop,
                    )}
            </View>
        );

        const secondaryRow = (
            <View>
                {isMaximized &&
                    this.renderModeratorOrOwnerActions(
                        profile,
                        styles.maximizedDesktop,
                    )}
            </View>
        );

        return (
            <View style={styles.postFooter}>
                {!isProgram && primaryRow}
                {secondaryRow}
            </View>
        );
    };

    expandAnswers: (postKey: string) => void = (postKey: string) => {
        this.setState({
            expandedKeys: [...this.state.expandedKeys, postKey],
        });
    };

    collapseAnswers: (postKey: string) => void = (postKey: string) => {
        this.setState({
            expandedKeys: this.state.expandedKeys.filter(
                (key) => key !== postKey,
            ),
        });
        this.postElement?.scrollIntoView({
            behavior: this.props.prefersReducedMotion ? "auto" : "smooth",
        });
    };

    handleToggleFormattingOptionsExpanded: (expanded: boolean) => void = (
        expanded: boolean,
    ) =>
        this.setState({
            formattingOptionsExpanded: expanded,
        });

    handleTogglePreviewExpanded: (expanded: boolean) => void = (
        expanded: boolean,
    ) =>
        this.setState({
            previewExpanded: expanded,
        });

    handleAnswerPostSuccess: (post: ServerPost, tab: FeedbackType) => void = (
        post: ServerPost,
        tab: FeedbackType,
    ) => {
        this.props.onPostSuccess(post, tab);
        this.hideReplyInputField();
    };

    handleEditingPost: (post: ServerPost) => void = (post: ServerPost) => {
        this.setState({editing: false});
    };

    hideReplyInputField: () => void = () => {
        this.props.onToggleReplyInput(this.props.post.key);
        this.setState({
            formattingOptionsExpanded: false,
        });
    };

    handleSelectionChange: (arg1: boolean) => void = (selected: boolean) => {
        const {onSelectPosts, onDeselectPost, post} = this.props;
        if (selected) {
            onSelectPosts([post.key]);
        } else {
            onDeselectPost(post.key);
        }
    };

    renderDefaultContent(): React.ReactNode {
        const {post} = this.props;
        const isReply =
            getPostTypeAllowingProgram(post)?.toLowerCase() === "reply";

        return (
            <Body
                style={[sharedStyles.content, isReply && styles.replyContent]}
            >
                <FormattedMarkdown string={post.content} />
            </Body>
        );
    }

    renderProjectEval(): React.ReactNode {
        const {post} = this.props;
        return <DiscussionEvaluation post={post} />;
    }

    renderProgramContent(): React.ReactNode {
        const {post, onSelectProgramPost, selectedProgramKey} = this.props;
        const {
            relativeUrl = "",
            title = "",
            flags,
            content,
            thumbnail,
            key,
        } = post;

        return (
            <View
                onClick={() => onSelectProgramPost(key)}
                style={key !== selectedProgramKey && styles.programHover}
            >
                <h2>
                    <a href={relativeUrl} target="_blank">
                        {title}
                    </a>
                </h2>
                <View>
                    <View style={styles.programViewInfo}>
                        <img
                            src={thumbnail}
                            alt={title}
                            width="200"
                            height="200"
                        />
                        <View style={styles.programCode}>
                            <pre>
                                <BodyMonospace>{content}</BodyMonospace>
                            </pre>
                        </View>
                    </View>
                    <ul>
                        {flags &&
                            flags.map((flag, i) => {
                                // The format of flag is "{type}: {reason}"
                                // Example: "plagarism: I created this"
                                const [flagType, ...rest] = flag.split(": ");
                                const flagMessage = rest.join(": ");

                                return (
                                    <li key={i}>
                                        <ProgramFlag
                                            flagType={flagType}
                                            message={flagMessage}
                                        />
                                    </li>
                                );
                            })}
                    </ul>
                </View>
            </View>
        );
    }

    renderProjectPreview(): null | React.ReactElement<
        React.ComponentProps<"img">
    > {
        const {post} = this.props;
        const {focus} = post;

        if (!focus || !focus.relativeUrl) {
            return null;
        }

        return (
            <img
                className={css(styles.projectPreview)}
                src={`${focus.relativeUrl}/latest.png`}
                alt={focus.translatedTitle}
            />
        );
    }

    renderContent(): React.ReactNode {
        const {post} = this.props;

        switch (getPostTypeAllowingProgram(post)) {
            case "scratchpad":
                return this.renderProgramContent();
            case "projectevalanswer":
                return this.renderProjectEval();
            case "projecteval":
            case "projecthelp":
                return (
                    <View>
                        {this.renderDefaultContent()}
                        {this.renderProjectPreview()}
                    </View>
                );
            default:
                return this.renderDefaultContent();
        }
    }

    render(): React.ReactNode {
        const {
            clarificationBeingAcceptedId,
            clarificationsEnabled,
            contentId,
            contentKind,
            displayInputPostKey,
            gqlClient,
            includeCheckbox,
            isAnswer,
            isMaximized,
            moderatorMode,
            newPostKey,
            onShowAnswers,
            onStopAcceptingClarification,
            post,
            profile,
            resetNewPostKeyInfo,
            restrictPosting,
            selected,
            tab,
            topicSlug,
            viewMode,
        } = this.props;
        const {
            answerCount,
            answers,
            author,
            authorAvatarName,
            authorAvatarSrc,
            authorKaid,
            authorNickname,
            content,
            fromVideoAuthor,
        } = post;
        const {editing, skipped, expandedKeys, repliesExpanded} = this.state;
        const acceptingClarification =
            post.key === clarificationBeingAcceptedId;
        const isReply = getPostTypeAllowingProgram(post) === "reply";

        if (skipped) {
            return null;
        }

        const postRef = (node: HTMLElement | null) => {
            if (this.props.getRef) {
                this.props.getRef(node);
            }
            this.postElement = node;
        };

        // HACK(chase): includeCheckboxes is used here to determine if the discussion-list
        // is being rendered from the flag-queue
        const onFlagQueue = includeCheckbox;

        return (
            <View testId="discussion-post">
                {this.renderOpenModals()}
                {!editing && !acceptingClarification && (
                    // We have a negative tabIndex on this View element so we can
                    // focus on when necessary, but we don't want it to show up
                    // in the tabbing order if the user is tabbing through the page.
                    <View
                        style={[
                            sharedStyles.post,
                            isReply &&
                                !onFlagQueue &&
                                styles.horizontalSeparator,
                        ]}
                        tabIndex={-1}
                        ref={postRef}
                    >
                        {this.shouldShowAuthor() && (
                            <View style={styles.avatar}>
                                <DiscussionAvatar
                                    username={
                                        author?.nickname ?? authorNickname
                                    }
                                    avatarName={
                                        author?.avatar?.name ?? authorAvatarName
                                    }
                                    avatarSrc={
                                        author?.avatar?.imageSrc ??
                                        authorAvatarSrc
                                    }
                                    kaid={author?.kaid ?? authorKaid}
                                    enableProfilePreviewTooltip={true}
                                />
                            </View>
                        )}
                        <View style={sharedStyles.everythingButTheAvatar}>
                            {this.renderPostHeader()}

                            <View style={styles.contentContainer}>
                                {fromVideoAuthor && (
                                    <LabelSmall style={styles.fromTheAuthor}>
                                        {t`From the author:`}
                                    </LabelSmall>
                                )}

                                {this.renderContent()}

                                {(post.appearsAsDeleted || post.deleted) && (
                                    <View
                                        style={[
                                            styles.contentBackdrop,
                                            post.deleted && styles.deletedPost,
                                            post.appearsAsDeleted &&
                                                !post.deleted &&
                                                styles.hiddenPost,
                                        ]}
                                    />
                                )}
                            </View>

                            {this.renderPostFooter()}

                            {!isReply && (
                                <ReplyList
                                    isExpanded={repliesExpanded}
                                    containerId={this.getRepliesContainerId()}
                                    postKey={post.key}
                                    replies={post.replies}
                                    tab={tab}
                                    topicSlug={topicSlug}
                                    newPostKey={newPostKey}
                                    resetNewPostKeyInfo={resetNewPostKeyInfo}
                                    contentKind={contentKind}
                                    contentId={contentId}
                                    gqlClient={gqlClient}
                                    viewMode={viewMode}
                                    isMaximized={this.props.isMaximized}
                                    moderatorMode={this.props.moderatorMode}
                                    restrictPosting={restrictPosting}
                                    isLocked={!!post.isLocked}
                                    onReplyPostSuccess={
                                        this.props.onPostSuccess
                                    }
                                />
                            )}
                        </View>
                    </View>
                )}
                {editing && (
                    <View style={styles.replyInputContainer}>
                        {/** HACK(kelsey): The post corresponding to
                    qa_expand_key will automatically show its replies.
                    However, we don't want to focus on the reply input which
                    is also shown. */}
                        <DiscussionInput
                            editing={editing}
                            postKey={post.key}
                            isReply={isReply}
                            isAnswer={isAnswer}
                            tab={tab}
                            // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
                            contentKind={contentKind}
                            contentId={contentId}
                            topicSlug={topicSlug}
                            feedbackKey={post.key}
                            resetNewPostKeyInfo={resetNewPostKeyInfo}
                            initialPostContent={content}
                            onPostSuccess={(
                                post: ServerPost,
                                tab: FeedbackType,
                            ) => this.handleEditingPost(post)}
                            onCancel={() => this.setState({editing: false})}
                            showAvatar={!this.isCompactView()}
                        />
                        {!isReply && (
                            <View style={{marginLeft: 48}}>
                                <ReplyList
                                    isExpanded={repliesExpanded}
                                    containerId={this.getRepliesContainerId()}
                                    postKey={post.key}
                                    replies={post.replies}
                                    tab={tab}
                                    topicSlug={topicSlug}
                                    newPostKey={newPostKey}
                                    resetNewPostKeyInfo={resetNewPostKeyInfo}
                                    contentKind={contentKind}
                                    contentId={contentId}
                                    gqlClient={gqlClient}
                                    viewMode={viewMode}
                                    isMaximized={this.props.isMaximized}
                                    moderatorMode={this.props.moderatorMode}
                                    restrictPosting={restrictPosting}
                                    isLocked={!!post.isLocked}
                                    onReplyPostSuccess={
                                        this.props.onPostSuccess
                                    }
                                />
                            </View>
                        )}
                    </View>
                )}
                {acceptingClarification && (
                    // @ts-expect-error - TS2741 - Property 'topicSlug' is missing in type '{ post: Post; editingOrAccepting: "accepting"; onCancel: () => void; }' but required in type 'ExportProps'.
                    <AcceptedClarificationInput
                        post={post}
                        editingOrAccepting="accepting"
                        onCancel={onStopAcceptingClarification}
                        gqlClient={gqlClient}
                    />
                )}
                {!isAnswer && !isReply && (
                    <AnswerList
                        answers={answers}
                        answerCount={answerCount || 0}
                        topLevelPostKey={post.key}
                        topicSlug={topicSlug}
                        newPostKey={newPostKey}
                        resetNewPostKeyInfo={resetNewPostKeyInfo}
                        tab={tab}
                        displayInputPostKey={displayInputPostKey}
                        restrictPosting={restrictPosting}
                        isLocked={!!post.isLocked}
                        clarificationsEnabled={clarificationsEnabled}
                        viewMode={viewMode}
                        authorKaid={post.author?.kaid ?? post.authorKaid}
                        moderatorMode={moderatorMode}
                        isMaximized={isMaximized}
                        onShowAnswers={() => onShowAnswers(post.key, gqlClient)}
                        profile={profile}
                        onAnswerPostSuccess={this.handleAnswerPostSuccess}
                        onToggleFormattingOptionsExpanded={
                            this.handleToggleFormattingOptionsExpanded
                        }
                        onTogglePreviewExpanded={
                            this.handleTogglePreviewExpanded
                        }
                        isCompactView={this.isCompactView()}
                        nodes={this.nodes}
                        expandAnswers={this.expandAnswers}
                        collapseAnswers={this.collapseAnswers}
                        isExpanded={expandedKeys.includes(post.key)}
                        displayReplyInput={displayInputPostKey === post.key}
                    />
                )}
                {includeCheckbox && (
                    <Checkbox
                        style={styles.checkbox}
                        checked={selected}
                        onChange={this.handleSelectionChange}
                        aria-label="Post selection"
                    />
                )}
            </View>
        );
    }
}

const gray = color.offBlack64;

const compactStyleSheet = StyleSheet.create(compactStyles);

const styles = StyleSheet.create({
    postHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 8,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    outerRepliesContainerWithAvatar: {
        // NOTE(slim): This is a special case not included with the rest of the
        // compact styles, because compact styles do not show avatars.
        [mediaQueries.xsOrSmaller]: {
            // Stretch the background to the left.
            // 16px spacing + 48px (the width of the avatar)
            marginLeft: -64,
        },
    },
    replyHeader: {
        marginBottom: 0,
    },
    replyContent: {
        paddingTop: spacing.xxxSmall_4,
    },
    replyInputContainer: {
        paddingBottom: spacing.large_24,
    },
    postedInContainer: {
        marginTop: spacing.xxxSmall_4,
        flexDirection: "row",
        alignItems: "baseline",
    },
    postedInLabel: {
        color: color.offBlack64,
        marginRight: spacing.xxSmall_6,
    },
    hiddenPostLabel: {
        color: color.gold,
        marginLeft: 8,
        alignSelf: "center",
    },
    deletedPostLabel: {
        color: color.red,
        marginLeft: 8,
        alignSelf: "center",
    },

    contentContainer: {
        position: "relative",
    },
    // We're using a backdrop element in order to be able to use our hex color constants
    // with a lower opacity.
    contentBackdrop: {
        width: "100%",
        height: "100%",
        borderRadius: 4,
        opacity: 0.1,
        position: "absolute",
        // This z-index just ensures that this backdrop sits behind the content.
        zIndex: -1,
    },
    hiddenPost: {
        backgroundColor: color.gold,
    },
    deletedPost: {
        backgroundColor: color.red,
    },
    postFooter: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        height: 40,
    },
    postFooterPrimary: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
    },
    votesSum: {
        color: gray,
        marginLeft: 12,

        [mediaQueries.xsOrSmaller]: {
            // These are defined outside of the `compactStyles` object because they should
            // be shown in compact view when the viewport is large.
            display: "none",
        },
    },
    voteText: {
        [mediaQueries.xsOrSmaller]: {
            // These are defined outside of the `compactStyles` object because we don't
            // display any vote controls in compact view, so these styles only apply to
            // the small viewport in the regular/expanded post view.
            display: "none",
        },
    },
    voteFlagList: {
        display: "flex",
    },
    toolButton: {
        marginLeft: 12,
    },
    flagTool: {
        [mediaQueries.xsOrSmaller]: {
            // TODO(briang): Once the flag tool makes it to the action picker,
            // we'll want to hide it here for mobile.
            //display: "none",
        },
    },
    flagText: {
        [mediaQueries.xsOrSmaller]: compactStyles.flagText,
    },
    moderatorFlagged: {
        marginLeft: 8,
    },
    fromTheAuthor: {
        marginLeft: 8,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    notificationsUnavailableText: {
        marginLeft: 12,
    },
    horizontalSeparator: {
        borderBottom: sharedStyles.border,
        clear: "both",
        paddingBottom: spacing.small_12,
        ":last-child": {
            borderBottom: "none",
            paddingBottom: 0,
        },
    },
    separator: {
        color: sharedStyles.borderColor,
        paddingLeft: spacing.xxxSmall_4,
        paddingRight: spacing.xxxSmall_4,
    },
    maximizedDesktop: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    expandedActionList: {
        flexDirection: "row",
    },
    avatar: {
        marginRight: 24,
    },
    programHover: {
        ":hover": {cursor: "pointer"},
    },
    programViewInfo: {
        display: "flex",
        flexDirection: "row",
        gap: 20,
    },
    programCode: {
        height: "200px",
        width: "100%",
        overflow: "scroll",
        backgroundColor: "#f0f0f0",
    },
    flagsTooltipRow: {
        flexDirection: "row",
    },
    flagsTooltipKaid: {
        color: color.blue,
    },
    flagsTooltipText: {
        paddingLeft: 8,
    },

    projectPreview: {
        width: 40,
    },
    checkbox: {
        // positions to under the avatar
        //
        // Note(Jhead): I would normally avoid position absolute but I think it
        // is the best solution here to line up the checkbox on the
        // flaggedfeedback moderation page without risking adding additional
        // layout and markup to the discussion post itself. this is ONLY
        // visible on moderation pages when the showCheckbox prop is passed in
        position: "absolute",
        top: 60,
        left: 15,
    },
    downvoteIcon: {
        transform: "rotate(180deg)",
    },
});

const mapDispatchToProps = {
    onChangeType: changeType,
    onClearFlags: clearFlags,
    onDeletePost: deletePost,
    onSkipPosts: skipPosts,
    onHideProgramEverywhere: hideProgramEverywhere,
    onHideProgramFromHotlist: hideProgramFromHotlist,
    onDownvotePost: downvotePost,
    onFlagPost: flagPost,
    onModifyNotifications: modifyNotificationsAction,
    onPostSuccess: postSuccessAction,
    onShowReplies: fetchRepliesPage,
    onShowAnswers: fetchAnswers,
    onToggleReplyInput: toggleReplyInputAction,
    onUndeletePost: undeletePost,
    onUpvotePost: upvotePost,
    resetExpandKey: resetExpandKeyAction,
    onStartAcceptingClarification: startAcceptingClarificationAction,
    onStopAcceptingClarification: stopAcceptingClarificationAction,
    onSelectPosts: selectPosts,
    onDeselectPost: deselectPost,
    onLockThread: lockThread,
    onUnlockThread: unlockThread,
    onPinThread: pinThread,
    onUnpinThread: unpinThread,
} as const;

type ProfileAnswerPost = Post & {
    question: ServerPost & {
        focus: NonNullable<ServerPost["focus"]>;
    };
};

// NOTE(slim): There are several main cases here.
//
// - Content discussions: All posts belong to the same content item, identified by `state.contentId` and `state.contentKind`.
//   Individual posts do not include the `focus` field.
//
// - Topic/Profile discussions: Each post has its own content item. `state.contentId` and `state.contentKind` are not meaningful (usually set to `""` and `"Topic"`, respectively).

// TODO(slim): All of this logic should be moved to the relevant API functions:
// - profile-package/profile-discussion-tab-contents.jsx
// - course-discussion-package/course-discussion.jsx

// TODO(slim): Account for flag queue.
const resolveContentItem = (
    allPosts: Partial<Record<PostKey, Post>>,
    globalContent: {
        contentKind: ContentKind;
        contentId: string;
    },
    post: Post | ProfileAnswerPost,
    passedInContent?: {
        contentKind: ContentKind;
        contentId: string;
    },
) => {
    // TODO(slim): Remove consumer-specific comments once logic is documented elsewhere.
    // Topic/Profile discussions, top-level (Questions, Feedback, Clarifications) and
    // Profile discussion, replies: use `focus` field.
    if (post.focus) {
        return {contentKind: post.focus.kind, contentId: post.focus.id};
    }
    if (post.question && post.question.focus) {
        // Profile discussion, answers: use `question` field.
        return {
            contentKind: post.question.focus.kind,
            contentId: post.question.focus.id,
        };
    }
    if (post.questionKey) {
        // Topic discussion, answers: use `questionKey` to get parent Question.
        const parent = allPosts[post.questionKey];
        if (parent && parent.focus) {
            return {contentKind: parent.focus.kind, contentId: parent.focus.id};
        }
    } else if (passedInContent) {
        // Topic discussion, replies: passed in by parent component.
        return {
            contentKind: passedInContent.contentKind,
            contentId: passedInContent.contentId,
        };
    }

    // Content discussion, all posts: belong to the same content item, use Redux state.
    return globalContent;
};

const mapStateToProps = (
    state: ReducerState,
    ownProps: DiscussionPostOwnProps,
): Partial<DiscussionPostProps> => {
    const post = state.posts[ownProps.postKey];
    const {contentKind, contentId} = resolveContentItem(
        state.posts,
        {contentKind: state.contentKind, contentId: state.contentId},
        post,
        // TODO(jeff, LP-6513: Property contentKind is missing in OwnProps
        // @ts-expect-error - TS2339 - Property 'contentKind' does not exist on type 'DiscussionPostOwnProps'.
        ownProps.contentKind
            ? {
                  // @ts-expect-error - TS2339 - Property 'contentKind' does not exist on type 'DiscussionPostOwnProps'.
                  contentKind: ownProps.contentKind,
                  // @ts-expect-error - TS2339 - Property 'contentId' does not exist on type 'DiscussionPostOwnProps'.
                  contentId: ownProps.contentId,
              }
            : undefined,
    );
    const showPostedIn = state.contentKind === "Topic";
    const showParentQuestion =
        showPostedIn && getPostTypeAllowingProgram(post) === "answer";
    const selected = state.selectedPosts[post.key] === true;

    return {
        post,
        contentId,
        contentKind,
        qaExpandKey: state.qaExpandKey,
        newPostKey: state.newPostKey,
        displayInputPostKey: state.displayInputPostKey,
        clarificationBeingAcceptedId: state.clarificationBeingAcceptedId,

        showPostedIn,
        showParentQuestion,

        selected,
    };
};

type ExportProps = WithoutLocation<
    WithoutUserProfile<
        WithoutApollo<
            WithoutActionScheduler<
                WithoutPrefersReducedMotion<
                    Without<
                        JSX.LibraryManagedAttributes<
                            typeof DiscussionPost,
                            React.ComponentProps<typeof DiscussionPost>
                        >,
                        DiscussionPostReduxProps
                    >
                >
            >
        >
    >
>;

const ConnectedDiscussionPost: React.ComponentType<ExportProps> = compose(
    withLocation,
    withUserProfile,
    withActionScheduler,
    withApollo,
    withPrefersReducedMotion,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionPost);

export default ConnectedDiscussionPost;
